import { env } from "@/config/env";
import type { ApiError, ApiResponse } from "@/types/api.types";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface RequestOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly type: ApiError["type"],
    public readonly code?: string,
    public readonly details?: ApiError["details"]
  ) {
    super(message);
    this.name = "ApiClientError";
  }

  toApiError(): ApiError {
    return {
      message: this.message,
      status: this.status,
      type: this.type,
      code: this.code,
      details: this.details,
    };
  }
}

class ApiClient {
  private baseURL: string;
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  async get<T>(endpoint: string, params?: object, options?: RequestOptions): Promise<T> {
    const url = this.buildUrl(endpoint, params);
    return this.request<T>("GET", url, undefined, options);
  }

  async post<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>("POST", endpoint, data, options);
  }

  /** POST returning the full ApiResponse envelope — use when the `message` matters to the caller. */
  async postWithMeta<T>(
    endpoint: string,
    data?: unknown,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    return this.requestEnvelope<T>("POST", endpoint, data, options);
  }

  async put<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>("PUT", endpoint, data, options);
  }

  async patch<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>("PATCH", endpoint, data, options);
  }

  async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>("DELETE", endpoint, undefined, options);
  }

  private buildUrl(endpoint: string, params?: object): string {
    if (!params) return endpoint;

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (typeof value === "object") {
          searchParams.append(key, JSON.stringify(value));
        } else if (
          typeof value === "string" ||
          typeof value === "number" ||
          typeof value === "boolean"
        ) {
          searchParams.append(key, String(value));
        }
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `${endpoint}?${queryString}` : endpoint;
  }

  private async request<T>(
    method: HttpMethod,
    endpoint: string,
    data?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    const result = await this.requestEnvelope<T>(method, endpoint, data, options);
    return result.data;
  }

  // Like request(), but returns the full ApiResponse envelope so callers can read `message`.
  private async requestEnvelope<T>(
    method: HttpMethod,
    endpoint: string,
    data?: unknown,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    const url = endpoint.startsWith("http") ? endpoint : `${this.baseURL}${endpoint}`;

    // Build headers with auth token if available
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // Add Authorization header from localStorage (unless manually provided)
    const accessToken = localStorage.getItem("tr_access_token");
    if (accessToken && !options?.headers?.Authorization) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    const fetchOptions: RequestInit = {
      method,
      headers: {
        ...headers,
        ...options?.headers,
      },
      credentials: "include", // Also send cookies as fallback
    };

    if (data) {
      fetchOptions.body = JSON.stringify(data);
    }

    if (options?.signal) {
      fetchOptions.signal = options.signal;
    }

    const response = await fetch(url, fetchOptions);

    // Handle 401 with token refresh
    if (response.status === 401 && !endpoint.includes("/auth/")) {
      const refreshed = await this.handleTokenRefresh();
      if (refreshed) {
        // Retry original request with new token (clear old Authorization header)
        const retryOptions = options ? { ...options } : {};
        delete retryOptions.headers?.Authorization;
        return this.requestEnvelope<T>(method, endpoint, data, retryOptions);
      }
      // Redirect to login if refresh failed
      window.location.href = "/login";
      throw this.createError(401, { message: "Session expired" });
    }

    if (!response.ok) {
      const errorBody = (await response.json().catch(() => ({}))) as Record<string, unknown>;
      throw this.createError(response.status, errorBody);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return { success: true, data: undefined as T, message: null, timestamp: "" };
    }

    return (await response.json()) as ApiResponse<T>;
  }

  private async handleTokenRefresh(): Promise<boolean> {
    // Prevent multiple simultaneous refresh requests
    if (this.isRefreshing) {
      return this.refreshPromise ?? Promise.resolve(false);
    }

    this.isRefreshing = true;
    this.refreshPromise = this.performRefresh();

    try {
      return await this.refreshPromise;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async performRefresh(): Promise<boolean> {
    try {
      const refreshToken = localStorage.getItem("tr_refresh_token");
      if (!refreshToken) {
        return false;
      }

      const response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        // Store new tokens from response
        const data = (await response.json()) as {
          data?: { access_token?: string; refresh_token?: string };
        };
        if (data.data?.access_token) {
          localStorage.setItem("tr_access_token", data.data.access_token);
        }
        if (data.data?.refresh_token) {
          localStorage.setItem("tr_refresh_token", data.data.refresh_token);
        }
        return true;
      }

      return false;
    } catch {
      return false;
    }
  }

  private createError(status: number, body: Record<string, unknown>): ApiClientError {
    const type = this.categorizeErrorType(status);
    // The backend envelope nests error info: { success: false, error: { code, message, details } }.
    // Fall back to top-level fields for non-enveloped responses.
    const nested = (
      typeof body.error === "object" && body.error !== null ? body.error : {}
    ) as Record<string, unknown>;
    return new ApiClientError(
      ((body.message ?? nested.message) as string | undefined) ?? this.getDefaultMessage(status),
      status,
      type,
      (body.code ?? nested.code) as string | undefined,
      (body.details ?? nested.details) as ApiError["details"]
    );
  }

  private categorizeErrorType(status: number): ApiError["type"] {
    switch (true) {
      case status === 401:
        return "authentication";
      case status === 403:
        return "authorization";
      case status === 404:
        return "not_found";
      case status === 422:
        return "validation";
      case status === 429:
        return "rate_limit";
      case status >= 500:
        return "server_error";
      default:
        return "unknown";
    }
  }

  private getDefaultMessage(status: number): string {
    const messages: Record<number, string> = {
      400: "Invalid request",
      401: "Please log in to continue",
      403: "You don't have permission to do this",
      404: "Resource not found",
      422: "Validation failed",
      429: "Too many requests. Please try again later",
      500: "Something went wrong. Please try again",
    };
    return messages[status] ?? "An unexpected error occurred";
  }
}

export const apiClient = new ApiClient(env.VITE_API_URL);
export { ApiClientError };
